import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { MODE_META, SHAPE_LABELS, type Shape } from '@clicklinx/protocol'
import { Icon } from '../components/Icon'
import { ShapeGlyph } from '../components/Shape'
import { formatCents } from '../lib/payments'
import { CoinChip, cx } from '../components/ui'
import { AnswerPad } from '../game/AnswerPad'
import { ClickRaceStage } from '../game/ClickRaceStage'
import { ScatterStage } from '../game/ScatterStage'
import { SequentialStage } from '../game/SequentialStage'
import { useAuth } from '../lib/auth'
import { formatAnswerMs, formatSeconds } from '../lib/format'
import { SEAT_COLORS } from '../lib/seats'
import { useGame } from '../lib/socket'
import { useCountdown } from '../lib/useCountdown'

const EASE = [0.22, 1, 0.36, 1] as const

export function MatchScreen() {
  const { user } = useAuth()
  const { room, phase, countdown, brief, display, collect, result, overtime, tick, toLocalTime, sendClicks, submitAnswer } =
    useGame()

  if (!room || !user) return null

  const me = room.players.find((p) => p.userId === user.id)
  const isEliminated = me?.eliminated ?? false

  return (
    <div className="flex min-h-dvh flex-col pad-safe pad-safe-top">
      {/* Header: pot, round, and every player's live standing. */}
      <header className="mx-auto flex w-full max-w-4xl shrink-0 items-center justify-between gap-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="eyebrow">{MODE_META[room.config.mode].name}</span>
          {room.roundIndex > 0 && (
            <span className="rounded-md bg-ink-800 px-2 py-0.5 font-mono text-xs font-semibold tnum text-fg-muted">
              R{room.roundIndex}
            </span>
          )}
        </div>
        {room.config.stake === 0 ? (
          <span className="eyebrow rounded-full border border-ink-700 px-2.5 py-1 text-fg-muted">
            Free round
          </span>
        ) : room.config.purse === 'prize' ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-lime/30 bg-lime/10 px-2.5 py-1 font-mono text-xs font-medium tnum text-lime">
            {formatCents(room.pot)}
          </span>
        ) : (
          <CoinChip amount={room.pot} tone="gold" size="sm" />
        )}
      </header>

      <div className="mx-auto w-full max-w-4xl shrink-0 pb-3">
        <div className="flex flex-wrap gap-2">
          {room.players.map((player) => (
            <div
              key={player.userId}
              className={cx(
                'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 transition-opacity duration-300',
                player.eliminated ? 'border-ink-800 opacity-35' : 'border-ink-700',
              )}
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: SEAT_COLORS[player.color] }}
              />
              <span className="max-w-[7rem] truncate text-xs font-medium text-fg">
                {player.userId === user.id ? 'You' : player.displayName}
              </span>
              {!player.connected && <Icon name="wifiOff" size={13} className="text-danger" />}
              {player.eliminated && <span className="eyebrow text-danger">out</span>}
            </div>
          ))}
        </div>
      </div>

      <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col justify-center pb-4">
        <AnimatePresence mode="wait">
          {phase === 'countdown' && countdown && (
            <PhaseShell key="countdown">
              <CountdownPhase startsAtLocal={toLocalTime(countdown.startsAt)} />
            </PhaseShell>
          )}

          {phase === 'overtime' && overtime && (
            <PhaseShell key="overtime">
              <div className="text-center">
                <p className="eyebrow text-danger">Dead heat</p>
                <h2 className="display-title mt-3 text-3xl text-fg sm:text-4xl">Overtime {overtime.overtimeIndex}</h2>
                <p className="mt-3 text-sm text-fg-muted">
                  Ten more seconds. Same pot.
                </p>
              </div>
            </PhaseShell>
          )}

          {phase === 'brief' && brief && (
            <PhaseShell key={`brief-${brief.roundIndex}`}>
              <BriefPhase
                roundIndex={brief.roundIndex}
                targetShape={brief.targetShape as Shape}
                targetLabel={brief.targetLabel}
                displayMs={brief.displayMs}
                mode={room.config.mode}
              />
            </PhaseShell>
          )}

          {phase === 'display' && display && (
            <PhaseShell key={`display-${display.round.roundIndex}-${display.startedAt}`}>
              {/* Driven by the round's own kind rather than the room config,
                  so the payload is self-describing. */}
              {display.round.kind === 'click_race' ? (
                <ClickRaceStage
                  startedAtLocal={toLocalTime(display.startedAt)}
                  durationMs={display.round.displayMs}
                  roundKey={display.startedAt}
                  players={room.players}
                  tick={tick}
                  myUserId={user.id}
                  onClicks={sendClicks}
                />
              ) : display.round.kind === 'count_sequential' ? (
                <SequentialStage
                  round={display.round}
                  startedAtLocal={toLocalTime(display.startedAt)}
                  roundKey={display.startedAt}
                />
              ) : (
                <ScatterStage
                  round={display.round}
                  startedAtLocal={toLocalTime(display.startedAt)}
                  roundKey={display.startedAt}
                />
              )}
            </PhaseShell>
          )}

          {phase === 'collect' && collect && (
            <PhaseShell key={`collect-${collect.roundIndex}`}>
              {isEliminated ? (
                <SpectatorNotice />
              ) : (
                <AnswerPad
                  deadlineLocal={toLocalTime(collect.deadlineAt)}
                  targetShape={(brief?.targetShape as Shape) ?? null}
                  targetLabel={brief ? brief.targetLabel : SHAPE_LABELS.circle.many}
                  totalAcrossScreens={room.config.mode === 'count_scatter'}
                  onSubmit={(value) => submitAnswer(collect.roundIndex, value)}
                />
              )}
            </PhaseShell>
          )}

          {phase === 'result' && result && (
            <PhaseShell key={`result-${result.roundIndex}`}>
              <ResultPhase />
            </PhaseShell>
          )}
        </AnimatePresence>
      </main>
    </div>
  )
}

function PhaseShell({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      className="flex min-h-0 flex-1 flex-col"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.26, ease: EASE }}
    >
      {children}
    </motion.div>
  )
}

function CountdownPhase({ startsAtLocal }: { startsAtLocal: number }) {
  const remaining = useCountdown(startsAtLocal)
  const seconds = formatSeconds(remaining)
  const reduceMotion = useReducedMotion()

  return (
    <div className="flex flex-1 flex-col items-center justify-center">
      <p className="eyebrow mb-6">Match starting</p>
      <AnimatePresence mode="popLayout">
        <motion.span
          key={seconds}
          className="font-mono text-[clamp(5rem,26vw,11rem)] font-bold leading-none tnum text-lime text-glow"
          initial={reduceMotion ? { opacity: 0 } : { scale: 0.5, opacity: 0 }}
          animate={reduceMotion ? { opacity: 1 } : { scale: 1, opacity: 1 }}
          exit={reduceMotion ? { opacity: 0 } : { scale: 1.5, opacity: 0 }}
          transition={{ duration: 0.34, ease: EASE }}
        >
          {seconds === '0' ? 'GO' : seconds}
        </motion.span>
      </AnimatePresence>
    </div>
  )
}

function BriefPhase({
  roundIndex,
  targetShape,
  targetLabel,
  displayMs,
  mode,
}: {
  roundIndex: number
  targetShape: Shape
  targetLabel: string
  displayMs: number
  mode: string
}) {
  const reduceMotion = useReducedMotion()

  return (
    <div className="flex flex-1 flex-col items-center justify-center text-center">
      <p className="eyebrow">Round {roundIndex}</p>

      <motion.div
        className="my-7 text-lime"
        initial={reduceMotion ? { opacity: 0 } : { scale: 0.6, opacity: 0, rotate: -8 }}
        animate={reduceMotion ? { opacity: 1 } : { scale: 1, opacity: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 320, damping: 20 }}
        style={{ filter: 'drop-shadow(0 0 34px rgba(215,255,62,0.4))' }}
      >
        <ShapeGlyph shape={targetShape} size={104} label={targetLabel} />
      </motion.div>

      <h2 className="display-title text-2xl text-fg sm:text-3xl">Count the {targetLabel}</h2>

      <p className="mt-4 max-w-sm text-sm leading-relaxed text-fg-muted">
        {mode === 'count_sequential'
          ? 'They will flash one after another. Count every appearance.'
          : 'The screen changes every few seconds. Add up every one you see across all of them.'}
      </p>

      <p className="mt-6 font-mono text-xs tnum text-fg-dim">
        {(displayMs / 1000).toFixed(1)}s on screen · 5.0s to answer
      </p>
    </div>
  )
}

function ResultPhase() {
  const { result, room } = useGame()
  const { user } = useAuth()
  if (!result || !room || !user) return null

  const nameOf = (userId: string) =>
    userId === user.id ? 'You' : (room.players.find((p) => p.userId === userId)?.displayName ?? 'Player')

  const myResult = result.results.find((r) => r.userId === user.id)

  return (
    <div className="flex flex-1 flex-col items-center justify-center">
      <p className="eyebrow">The answer was</p>
      <motion.p
        className="mt-2 font-mono text-7xl font-bold tnum text-lime text-glow sm:text-8xl"
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 340, damping: 22 }}
      >
        {result.correctAnswer}
      </motion.p>

      <div className="mt-8 w-full max-w-sm space-y-2">
        {result.results.map((entry, index) => (
          <motion.div
            key={entry.userId}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.12 + index * 0.06, duration: 0.24, ease: EASE }}
            className={cx(
              'flex items-center justify-between rounded-xl border px-4 py-3',
              entry.correct ? 'border-lime/35 bg-lime-wash' : 'border-danger/30 bg-danger/8',
            )}
          >
            <span className="flex items-center gap-2.5">
              <Icon
                name={entry.correct ? 'check' : 'close'}
                size={16}
                className={entry.correct ? 'text-lime' : 'text-danger'}
              />
              <span className="text-sm font-medium text-fg">{nameOf(entry.userId)}</span>
            </span>
            <span className="flex items-center gap-3">
              <span className="font-mono text-sm tnum text-fg-muted">
                {entry.answer ?? '—'}
              </span>
              <span className="font-mono text-[0.6875rem] tnum text-fg-dim">
                {formatAnswerMs(entry.answerMs)}
              </span>
            </span>
          </motion.div>
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.45 }}
        className="mt-7 max-w-sm text-center text-sm text-fg-muted"
      >
        {result.allSameOutcome ? (
          <>
            Everyone{' '}
            <span className="text-fg">{result.results[0]?.correct ? 'got it' : 'missed it'}</span> —
            nobody is out. Next round runs faster.
          </>
        ) : result.eliminated.length > 0 ? (
          <>
            <span className={myResult?.correct ? 'text-lime' : 'text-danger'}>
              {result.eliminated.map(nameOf).join(', ')}
            </span>{' '}
            {/* "You" takes a plural verb, so a lone eliminated viewer reads
                "You are out", not "You is out". */}
            {result.eliminated.length === 1 && result.eliminated[0] !== user.id ? 'is' : 'are'} out.
          </>
        ) : null}
      </motion.p>
    </div>
  )
}

function SpectatorNotice() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center text-center">
      <Icon name="target" size={32} className="text-fg-dim" />
      <p className="display-title mt-4 text-sm text-fg-muted">You are out</p>
      <p className="mt-2 max-w-xs text-sm text-fg-dim">
        Watching the rest play it out. The pot goes to whoever survives.
      </p>
    </div>
  )
}
